/**
 * Button — the design system's text button, with a click ripple.
 *
 * Ported from `tw-components.jsx`; the intended deviations are mechanical only (`cx` and
 * `Icon` are imported, no `import React`).
 *
 * ⚠️ It was once **not** a 1:1 port, and the drift was invisible until the first
 * `outline` consumer appeared. Five undocumented deviations were found against *both*
 * upstream copies and corrected:
 *
 * - `outline` had `border-line` without upstream's `!`. The base classes set
 *   `border-transparent`, and Tailwind resolves that conflict by **stylesheet order,
 *   not class order** — so outline buttons rendered with no border at all. The `!` is
 *   load-bearing; keep it.
 * - Radius was `--radius-control` (9px); upstream is `--radius-pill`.
 * - `primary`/`secondary` had invented `enabled:hover:shadow-brand`/`shadow-ocean`.
 * - The base classes had an invented `enabled:hover:-translate-y-[2px]` lift.
 * - `tonal` matched the older handoff copy (orange-50). The two upstream copies disagree
 *   here; the top-level file is the authority (neutral-100) and is what's used.
 *
 * **Not a replacement for `common/ui/Button.js`.** That one is the canonical button
 * for the ~everything already built on it; this is for new DS-spec surfaces. Don't
 * bulk-swap call sites — the two have different prop names (`startIcon` vs the legacy
 * component's own API) and different variant vocabularies.
 *
 * Needs `dnd-ripple` and `dnd-spin` from ./keyframes.
 */
import { cx } from "./cx";
import { Icon } from "./Icon.jsx";
import "./keyframes";

const BTN_SIZE = {
  sm: "h-[var(--control-height-sm)] px-[var(--space-4)] text-sm",
  md: "h-[var(--control-height-md)] px-[var(--space-6)] text-base",
  lg: "h-[var(--control-height-lg)] px-[var(--space-8)] text-md",
};

const BTN_VAR = {
  primary:
    "bg-[var(--action-primary)] text-white enabled:hover:bg-[var(--action-primary-hover)] enabled:active:bg-[var(--action-primary-active)]",
  secondary:
    "bg-[var(--action-secondary)] text-white enabled:hover:bg-[var(--action-secondary-hover)] enabled:active:bg-[var(--action-secondary-active)]",
  tonal:
    "bg-neutral-100 text-orange-700 enabled:hover:bg-neutral-200 enabled:active:bg-neutral-300",
  outline:
    "bg-transparent text-ink !border-line enabled:hover:!border-blue-600 enabled:hover:text-blue-700 enabled:hover:bg-blue-50",
  ghost:
    "bg-transparent text-ink enabled:hover:bg-[var(--action-neutral-hover)] enabled:active:bg-[var(--action-neutral-active)]",
  danger: "bg-error-500 text-white enabled:hover:bg-error-600",
};

const BTN_TRANSITION =
  "[transition:transform_var(--duration-base)_var(--ease-spring),box-shadow_var(--duration-base)_var(--ease-out),background_var(--duration-fast)_var(--ease-standard),border-color_var(--duration-fast)_var(--ease-standard),color_var(--duration-fast)_var(--ease-standard)]";

const BTN_ICON_SIZE = { sm: 18, md: 20, lg: 22 };

export function Button({
  children,
  variant = "primary",
  size = "md",
  startIcon,
  endIcon,
  loading = false,
  fullWidth = false,
  disabled = false,
  as = "button",
  onClick,
  style = {},
  className = "",
  ...rest
}) {
  const Tag = as;
  const isDisabled = disabled || loading;
  const handleClick = (e) => {
    if (isDisabled) return;
    const btn = e.currentTarget,
      rect = btn.getBoundingClientRect(),
      d = Math.max(rect.width, rect.height);
    const r = document.createElement("span");
    r.className =
      "dnd-btn__ripple absolute rounded-full scale-0 pointer-events-none bg-current opacity-[0.28] animate-[dnd-ripple_var(--duration-slow)_var(--ease-out)_forwards]";
    r.style.width = r.style.height = `${d}px`;
    r.style.left = `${e.clientX - rect.left - d / 2}px`;
    r.style.top = `${e.clientY - rect.top - d / 2}px`;
    btn.appendChild(r);
    setTimeout(() => r.remove(), 600);
    if (onClick) onClick(e);
  };
  const renderIcon = (ic) =>
    typeof ic === "string" ? (
      <Icon name={ic} size={BTN_ICON_SIZE[size]} weight={400} />
    ) : (
      ic
    );
  return (
    <Tag
      className={cx(
        "dnd-btn relative inline-flex items-center justify-center gap-[var(--space-2)] font-sans font-semibold tracking-[-0.005em] whitespace-nowrap border-[1.5px] border-transparent rounded-[var(--radius-pill)] cursor-pointer select-none overflow-hidden no-underline",
        BTN_TRANSITION,
        "enabled:active:scale-[0.97] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] disabled:cursor-not-allowed disabled:opacity-[0.55]",
        BTN_SIZE[size] || BTN_SIZE.md,
        BTN_VAR[variant] || BTN_VAR.primary,
        fullWidth && "w-full",
        className
      )}
      onClick={handleClick}
      disabled={Tag === "button" ? isDisabled : undefined}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      style={style}
      {...rest}
    >
      {loading && (
        <span
          className="w-[1em] h-[1em] rounded-full border-2 border-current border-t-transparent animate-[dnd-spin_0.7s_linear_infinite]"
          aria-hidden="true"
        />
      )}
      {!loading && startIcon && renderIcon(startIcon)}
      {children && <span>{children}</span>}
      {!loading && endIcon && renderIcon(endIcon)}
    </Tag>
  );
}
